'use client';

import type {FormEvent, ReactNode} from 'react';
import React, {
	type Dispatch,
	type FC,
	type InputHTMLAttributes,
	type SetStateAction,
	useCallback,
	useEffect,
	useId,
	useRef,
} from 'react';
import {Bars3Icon, XMarkIcon} from '@heroicons/react/20/solid';
import clsx from 'clsx';
import Link from 'next/link';
import {useRouter} from 'next/navigation';
import {twMerge} from 'tailwind-merge';

import {buttonClasses} from '@/components/button';
import {Logo} from '@/components/icons/logo';
import {searchInputClasses, SearchInputPrefix} from '@/components/input';
import {i18n} from '@/utils/i18n';

import {useHero} from '../page-head/context/page-head-context';

interface Props {
	menu?: ReactNode;
	menuOpen: boolean;
	setMenuOpen?: Dispatch<SetStateAction<boolean>>;
	hasOpenedMenu: boolean;
	searchValue: string;
	setSearchValue?: Dispatch<SetStateAction<string>>;
	link365?: ReactNode;
}

export const Header: FC<Props> = ({
	menu,
	menuOpen,
	hasOpenedMenu,
	setMenuOpen,
	searchValue,
	setSearchValue,
	link365,
}) => {
	const openButtonRef = useRef<HTMLButtonElement>(null);
	const closeButtonRef = useRef<HTMLButtonElement>(null);

	const {darkHero} = useHero();

	useEffect(() => {
		const closeMenu = (event: KeyboardEvent) => {
			// eslint-disable-next-line @typescript-eslint/no-unused-expressions
			event.key === 'Escape' && setMenuOpen && setMenuOpen(false);
		};

		window.addEventListener('keydown', closeMenu);
		return () => window.removeEventListener('keydown', closeMenu);
	}, [menuOpen, setMenuOpen]);

	useEffect(() => {
		if (menuOpen) {
			closeButtonRef.current?.focus();
		} else if (hasOpenedMenu) {
			openButtonRef.current?.focus({
				preventScroll: true,
			});
		}
	}, [menuOpen, hasOpenedMenu]);

	return (
		<header className={clsx('flex', 'w-full', 'justify-center')}>
			<a
				href="#maincontent"
				className={clsx(
					'absolute',
					'left-[-999px]',
					'w-px',
					'h-px',
					'top-auto',
					'focus:top-2',
					'focus:left-2',
					'focus:w-auto',
					'focus:h-auto',
					'focus:m-auto',
					'focus:inline-block',
					'focus:px-3',
					'focus:py-1',
					'focus:bg-beige-500',
					'shadow-none',
					'focus:z-50',
					'ring-forestGreen-700',
					'ring',
				)}
			>
				{i18n.components.header.skipToMainContent}
			</a>
			<nav
				className={clsx(
					'max-w-screen-xl',
					'w-full',
					'overflow-hidden',
					'flex',
					'flex-col',
					menuOpen && 'h-dvh',
				)}
			>
				<div
					className={clsx(
						'grid',
						'w-full',
						'px-6',
						'gap-x-8',
						'gap-y-4',
						'items-center',
						'grid-cols-[1fr_max-content]',
						menuOpen
							? ['grid-rows-[112px_1fr]', 'lg:grid-rows-[176px]']
							: ['grid-rows-[112px]', 'lg:grid-rows-[176px]'],
						!menuOpen &&
							'lg:grid-cols-[max-content_1fr_max-content]',
					)}
				>
					<div className={clsx('py-7', 'lg:py-14', 'h-full')}>
						<Link
							href="/"
							className={clsx(
								'inline-block',
								darkHero
									? 'text-turquoise-700'
									: 'text-forestGreen-700',
								'h-full',
								'p-4',
								'-m-4',
								'box-content',
								'rounded',
								menuOpen && 'hidden',
							)}
						>
							<span className={clsx('sr-only')}>
								{i18n.main.gjovikregionen}
							</span>
							<Logo />
						</Link>
					</div>

					<div
						className={clsx(
							'w-full',
							menuOpen
								? [
										'block',
										'lg:hidden',
										'col-span-2',
										'row-start-2',
										'mb-16',
									]
								: ['hidden', 'lg:flex', 'justify-end'],
						)}
					>
						{!menuOpen && link365}

						<SearchInput
							menuOpen={menuOpen}
							value={searchValue}
							setValue={setSearchValue}
						/>
					</div>

					<div className={clsx('flex', 'justify-end')}>
						{menuOpen ? (
							<button
								className={clsx(
									buttonClasses,
									'items-center',
									'gap-4',
									'!text-bodyMedium',
									'p-3',
									'-m-3',
									'text-turquoise-700',
									'lg:m-0',
									'lg:!px-8',
									'lg:rounded-[3.5rem]',
									'rounded-md',
									'lg:text-turquoise-700',
									'lg:hover:rounded-lg',
									'lg:border',
									'lg:border-turquoise-700',
									'focus-visible:ring-turquoise-700',
									'focus-visible:ring-offset-forestGreen-700',
									'!px-3',
									'aspect-square',
									'lg:aspect-auto',
									'lg:h-12',
									'h-auto',
								)}
								onClick={() =>
									setMenuOpen && setMenuOpen(false)
								}
								ref={closeButtonRef}
							>
								<div className={clsx('hidden', 'lg:block')}>
									Meny
								</div>
								<XMarkIcon
									className={clsx(
										'h-8',
										'lg:-mr-3',
										'lg:h-6',
									)}
								/>
							</button>
						) : (
							<button
								className={twMerge(
									buttonClasses,
									clsx(
										'gap-4',
										'!text-bodyMedium',
										'p-3',
										'-m-3',
										'lg:m-0',
										'lg:!px-8',
										'lg:rounded-[3.5rem]',
										'rounded-md',
										'lg:bg-forestGreen-700',
										'lg:text-turquoise-700',
										'lg:hover:rounded-lg',
										'bg-beige-500',
										'text-forestGreen-700',
										'!px-3',
										'aspect-square',
										'lg:aspect-auto',
										'lg:h-12',
										'h-auto',
									),
								)}
								onClick={() => setMenuOpen && setMenuOpen(true)}
								ref={openButtonRef}
							>
								<div className={clsx('hidden', 'lg:block')}>
									{i18n.components.header.menu}
								</div>
								<Bars3Icon
									className={clsx(
										'h-8',
										'lg:-mr-3',
										'lg:h-6',
									)}
								/>
							</button>
						)}
					</div>
				</div>
				{menuOpen && (
					<div className={clsx('flex-1', 'overflow-auto')}>
						{menu}
					</div>
				)}
			</nav>
		</header>
	);
};

interface SearchInputProps extends InputHTMLAttributes<HTMLInputElement> {
	menuOpen: boolean;
	value: string;
	setValue?: Dispatch<SetStateAction<string>>;
}

const SearchInput: FC<SearchInputProps> = ({menuOpen, value, setValue}) => {
	const id = useId();
	const router = useRouter();

	const handleSubmit = useCallback(
		(event: FormEvent<HTMLFormElement>) => {
			event.preventDefault();
			router.push(`/sok?q=${new URLSearchParams({q: value}).get('q')}`);
		},
		[router, value],
	);

	return (
		<form onSubmit={handleSubmit}>
			{}
			<label
				className={clsx(
					'relative',
					'block',
					'w-full',
					'transition-all',
					'duration-200',
					!menuOpen && [
						value.length > 0 ? 'max-w-sm' : 'max-w-32',
						'focus-within:max-w-sm',
					],
					menuOpen ? 'text-turquoise-700' : 'text-forestGreen-700',
				)}
			>
				<input
					id={id}
					type="text"
					placeholder={i18n.components.search.placeholder}
					value={value}
					onChange={(event) =>
						setValue && setValue(event.target.value)
					}
					className={twMerge(
						searchInputClasses,
						clsx(
							'!rounded-[3.5rem]',
							'hover:!rounded-xl',
							'focus:!rounded-2xl',
							'w-full',
							menuOpen
								? [
										'!border-turquoise-700',
										'active:text-turquoise-700',
										'placeholder:text-turquoise-700',
										'focus-visible:ring-turquoise-700',
										'focus-visible:ring-offset-forestGreen-700',
									]
								: [
										'!border-forestGreen-700',
										'active:text-forestGreen-700',
										'placeholder:text-[#527A76]',
										'bg-beige-500',
									],
						),
					)}
				/>
				<SearchInputPrefix />
			</label>
		</form>
	);
};
